import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchActiveCramSession,
  fetchBuilderSettings,
  startCramSession,
  type ExerciseKind,
} from '@features/Session';
import { paths } from '@shared/config';
import type { ModuleSummary } from '@shared/types';
import { toast } from '@shared/ui';
import type { BuilderDefaults } from '../model/builderConfig';
import { BuilderPanel } from './BuilderPanel';
import './BuilderPanel.css';

interface CramPickerProps {
  module: ModuleSummary;
  onBack: () => void;
  /** Откуда пришли: туда «назад» и туда тренировка вернёт после итогов. */
  returnTo?: 'dashboard' | 'module' | 'words';
  /** Состав из адреса (`?preset=warmup&n=10&boss=0` — ссылка из письма), см. BuilderDefaults. */
  defaults?: BuilderDefaults;
}

/** Общий конструктор тренировки для входов с главной и из модуля. */
export function CramPicker({ module: m, onBack, returnTo = 'module', defaults }: CramPickerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [editing, setEditing] = useState(false);
  const startingRef = useRef(false);
  const backLabel = returnTo === 'dashboard' ? t('training.chooseModule') : returnTo === 'words' ? t('words.title') : m.title;

  const activeQuery = useQuery({
    queryKey: ['cram-active', m.id],
    queryFn: () => fetchActiveCramSession(m.id),
    staleTime: 0,
    gcTime: 0,
  });
  const settingsQuery = useQuery({
    queryKey: ['cram-builder-settings', m.id],
    queryFn: () => fetchBuilderSettings(m.id),
    staleTime: 0,
    gcTime: 0,
  });

  const goToSession = () => {
    navigate(`${paths.session}?moduleId=${encodeURIComponent(m.id)}`, {
      state: { cram: true, fromModuleId: m.id, fromDashboard: returnTo === 'dashboard', fromWords: returnTo === 'words' },
    });
  };

  const startBuilder = async (
    exercises: ExerciseKind[],
    stateIds: string[] | undefined,
    withBoss: boolean,
  ) => {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    try {
      await startCramSession(m.id, 'builder', { exercises, stateIds, withBoss });
      goToSession();
    } catch {
      startingRef.current = false;
      setStarting(false);
      toast({ tone: 'error', message: t('cram.startError') });
    }
  };

  if (
    activeQuery.isPending ||
    settingsQuery.isPending ||
    activeQuery.isError ||
    settingsQuery.isError
  ) {
    const failed = activeQuery.isError || settingsQuery.isError;
    return (
      <div className="bx bx-thin">
        <button type="button" onClick={onBack} className="bx-back">
          ← {backLabel}
        </button>
        <p className="bx-note" role={failed ? 'alert' : 'status'}>
          {failed ? t('cram.loadError', 'Не удалось загрузить тренировку.') : t('cram.builder.loading')}
        </p>
        {failed && (
          <button
            type="button"
            className="bx-resume-secondary"
            onClick={() => {
              void activeQuery.refetch();
              void settingsQuery.refetch();
            }}
          >
            {t('cram.retry', 'Попробовать снова')}
          </button>
        )}
      </div>
    );
  }

  const active = activeQuery.data;
  if (active && !editing) {
    return (
      <div className="bx bx-thin">
        <button type="button" onClick={onBack} className="bx-back">
          ← {backLabel}
        </button>
        <div className="bx-resume">
          <p className="bx-eyebrow">
            <span>{t('cram.kicker')}</span>
            <i aria-hidden>/</i>
            <span>{m.title}</span>
          </p>
          <h1>{t('cram.resumeTitle', 'Тренировка сохранена')}</h1>
          <p className="bx-resume-sub">
            {active.status === 'is_ready_to_boss'
              ? t('cram.resumeFinal', 'Остался финальный матч — собери слова в пары.')
              : t('cram.resumeRemaining', {
                  count: active.remaining,
                  defaultValue:
                    'Осталось карточек: {{count}}. Продолжи с того места, где остановился.',
                })}
          </p>
          <button type="button" className="bx-start" onClick={goToSession}>
            {t('cram.resume', 'Продолжить тренировку')}
          </button>
          <button type="button" className="bx-resume-secondary" onClick={() => setEditing(true)}>
            {t('cram.changeSetup', 'Собрать новую тренировку')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <BuilderPanel
      key={m.id}
      moduleId={m.id}
      moduleTitle={m.title}
      backLabel={active ? t('cram.resumeTitle', 'Тренировка сохранена') : backLabel}
      starting={starting}
      replacingActive={!!active}
      savedSettings={settingsQuery.data ?? null}
      defaults={defaults}
      onBack={active ? () => setEditing(false) : onBack}
      onStart={startBuilder}
    />
  );
}
